import cn from 'classnames';
import get from 'lodash/get';
import React, { memo } from 'react';

import { Typography } from '@sravni/react-design-system';
import { useIsMobile } from '@sravni/react-utils';
import { format } from '@sravni/utils/lib/money';
import type IProduct from '@sravni/vitrina/lib/types/IProduct';


import { useProductConfig } from '../../../../../../store/hooks/productPopupInfo';

import style from './style.module.scss';
import Tooltip from './Tooltip';

const HEAD = 'Как быстро окупится обучение';
const SUBHEAD =
  'Показываем, как будет расти ваш заработок вместе с опытом. И сколько времени потребуется, чтобы окупить вложения в образование';

interface IProps {
  product: IProduct;
}

const Salary: React.FC<IProps> = memo(({ product }) => {
  const config = useProductConfig();
  const {
    list: { desktop, mobile },
    dictionaries,
  } = config;
  const isMobile = useIsMobile();
  const configMap = isMobile ? mobile : desktop;

  const { salary } = configMap.productInfo;
  const professionals = get(product, salary?.profValues);
  const [professional] = Array.isArray(professionals) ? professionals : [professionals];
  const dictionary = get(dictionaries, salary?.profDictionaries);

  const data = dictionary?.find((d) => d.value === professional);
  const professionName = get(data, salary?.dictionariesFields.professionName);
  const salaryFrom = get(data?.meta, salary?.dictionariesFields.salaryFrom);
  const salaryTo = get(data?.meta, salary?.dictionariesFields.salaryTo);

  const levels = get(product, salary?.profLevel);
  const isJunior = levels?.includes('levelJuniorNew');
  const isMiddle = levels?.includes('levelMiddleNew');
  const salaryMiddle = ((Number(salaryFrom) + Number(salaryTo)) / 2).toString();

  if (!professionName || !salaryFrom || !salaryTo || !levels) {
    return null;
  }

  function salaryByGrade() {
    if (isJunior) {
      return format(salaryFrom);
    }
    if (isMiddle) {
      return format(salaryMiddle);
    }
    return format(salaryTo);
  }

  return (
    <div className={cn(style.wrapper)}>
      <div className={style.header}>
        <Typography.Heading level={4}>{HEAD}</Typography.Heading>
        <Typography.Text size={14} className={cn(style.subhead)}>
          {SUBHEAD}
        </Typography.Text>
      </div>
      <div className={style.wrapperName}>
        <div className={style.name}>{professionName}</div>
        <div className={style.sum}>{`${format(salaryFrom)}\u00A0₽ — ${format(salaryTo)}\u00A0₽`}</div>
      </div>
      {isMobile && <Tooltip sum={`${salaryByGrade()}\u00A0₽`} />}
      <div className={style.salary}>
        <div className={style.lineWrapper}>
          <div className={style.line}>
            <div className={cn(style.dot, style.dot_1)}>
              {!isMobile && isJunior && <Tooltip sum={`${salaryByGrade()} ₽`} />}
            </div>
            <div className={cn(style.dot, style.dot_2)}>
              {!isMobile && !isJunior && isMiddle && <Tooltip sum={`${salaryByGrade()}\u00A0₽`} />}
            </div>
            <div className={cn(style.dot, style.dot_3)} />
          </div>
          <div className={style.grade}>
            <div>
              <div className={style.gradeName}>Junior</div>
              <div className={style.experience}>Опыт до 1 года</div>
            </div>
            <div>
              <div className={style.gradeName}>Middle</div>
              <div className={style.experience}>Опыт 1-3 года</div>
            </div>
            <div>
              <div className={style.gradeName}>Senior</div>
              <div className={style.experience}>Опыт от 4 лет</div>
            </div>
          </div>
        </div>

        <div />
      </div>
    </div>
  );
});

export default Salary;
